import type { Component } from "solid-js";
import { Show } from "solid-js";
import { useTranslation } from "../../../../state/i18n";
import { useCustomerState } from "../../../../state/customer";
import PermissionService from "../../../../services/PermissionService";
import { SidebarItem } from "./SidebarItem";
import { Breadcrumb } from "../../../../components/breadcrumb/Breadcrumb";
import SeparatorLine from "../../../../components/SeparatorLine";
import FeatureService from "../../../../services/FeatureService";
import SidebarBannerSection from "./SidebarBannerSection";

const SidebarSettings: Component = () => {
  const { t } = useTranslation();
  const customer = useCustomerState();

  const canAccessWebhooks = () =>
    PermissionService.canManageWebhooks(customer.customerId) && FeatureService.hasFeature("WEBHOOKS");
  const canAccessEmailConfiguration = () =>
    PermissionService.canManageEmailConfiguration(customer.customerId) && FeatureService.hasFeature("SENDING_DOMAIN");
  const canAccessSso = () =>
    PermissionService.canManageSSO(customer.customerId) && FeatureService.hasFeature("SINGLE_SIGN_ON");

  return (
    <div class="mt-3.5 flex h-full flex-col">
      <div class="mb-6 ml-1 px-2">
        <Breadcrumb
          crumbs={[
            { label: t(($) => $.SIDEBAR_SETTINGS_BREADCRUMB_HOME_LABEL), href: `/customers/${customer.customerId}` },
          ]}
          currentLabel={t(($) => $.SIDEBAR_SETTINGS_BREADCRUMB_SETTINGS_LABEL)}
        />
      </div>

      <div class="flex flex-col gap-6 overflow-y-auto px-2 pb-6">
        <div class="flex flex-col gap-1">
          <h3 class="ml-2.5 text-header-md text-secondary">{t(($) => $.SETTINGS_SECTION_ACCOUNT_LABEL)}</h3>
          <SidebarItem
            label={t(($) => $.SETTINGS_ITEM_PROFILE_LABEL)}
            icon="user"
            href={`/customers/${customer.customerId}/settings/profile`}
          />
          <Show when={PermissionService.canManageCompanyInformation(customer.customerId)}>
            <SidebarItem
              label={t(($) => $.SETTINGS_ITEM_COMPANY_INFORMATION_LABEL)}
              icon="officeBuilding"
              href={`/customers/${customer.customerId}/settings/company`}
            />
          </Show>
        </div>

        <Show when={PermissionService.canManageBilling(customer.customerId)}>
          <SeparatorLine />

          <div class="flex flex-col gap-1">
            <h3 class="ml-2.5 text-header-md text-secondary">{t(($) => $.SETTINGS_SECTION_BILLING_LABEL)}</h3>
            <SidebarItem
              label={t(($) => $.SETTINGS_ITEM_BILLING_AND_PAYMENT_LABEL)}
              icon="creditCard"
              href={`/customers/${customer.customerId}/settings/billing`}
            />
            <SidebarItem
              label={t(($) => $.SETTINGS_ITEM_INVOICES_LABEL)}
              icon="documentText"
              href={`/customers/${customer.customerId}/settings/invoices`}
            />
            <SidebarItem
              label={t(($) => $.SETTINGS_ITEM_USAGE_LABEL)}
              icon="chartBar"
              href={`/customers/${customer.customerId}/settings/usage`}
            />
          </div>
        </Show>

        <Show
          when={
            PermissionService.canManageApiKeys(customer.customerId) ||
            canAccessWebhooks() ||
            canAccessEmailConfiguration() ||
            PermissionService.canManageServiceAccounts(customer.customerId) ||
            canAccessSso()
          }
        >
          <SeparatorLine />

          <div class="flex flex-col gap-1">
            <h3 class="ml-2.5 text-header-md text-secondary">{t(($) => $.SETTINGS_SECTION_CONNECTIONS_LABEL)}</h3>
            <Show when={PermissionService.canManageApiKeys(customer.customerId)}>
              <SidebarItem
                label={t(($) => $.SETTINGS_ITEM_API_KEYS_LABEL)}
                icon="lockClosed"
                href={`/customers/${customer.customerId}/settings/api-keys`}
              />
            </Show>
            <Show when={canAccessWebhooks()}>
              <SidebarItem
                label={t(($) => $.SETTINGS_ITEM_WEBHOOKS_LABEL)}
                icon="webhook"
                href={`/customers/${customer.customerId}/settings/webhooks`}
              />
            </Show>
            <Show when={canAccessEmailConfiguration()}>
              <SidebarItem
                label={t(($) => $.SETTINGS_ITEM_EMAIL_CONFIGURATION_LABEL)}
                icon="mail"
                href={`/customers/${customer.customerId}/settings/email-configuration`}
              />
            </Show>
            <Show when={PermissionService.canManageServiceAccounts(customer.customerId)}>
              <SidebarItem
                label={t(($) => $.SETTINGS_ITEM_SERVICE_ACCOUNTS_LABEL)}
                icon="userCircle"
                href={`/customers/${customer.customerId}/settings/service-accounts`}
              />
            </Show>
            <Show when={canAccessSso()}>
              <SidebarItem
                label={t(($) => $.SETTINGS_ITEM_SSO_LABEL)}
                icon="key"
                href={`/customers/${customer.customerId}/settings/sso`}
              />
            </Show>
          </div>
        </Show>
      </div>

      <SidebarBannerSection classList={{ "px-2": true }} />
    </div>
  );
};

export default SidebarSettings;
